import type { ProductContext } from "../types";

export const PRODUCT_SCHEMA_ID = "indexaai-product-schema";

interface SchemaResponse {
  schema: Record<string, unknown>[] | null;
}

type SchemaNodes = Record<string, unknown>[] | null;

interface ParsedSchemaScript {
  script: HTMLScriptElement;
  originalText: string;
  value: unknown;
  schemaNodes: Record<string, unknown>[];
}

const REMOVE_NODE = Symbol("remove-json-ld-node");

const modifiedPageSchemaScripts: Array<{
  script: HTMLScriptElement;
  originalText: string;
  mergedText: string;
}> = [];
const addedMicrodataElements: Array<{ root: Element; elements: Element[] }> = [];

let pageSchemaObserver: MutationObserver | null = null;
let activePageSchema: { nodes: Record<string, unknown>[]; product: ProductContext } | null = null;

// Resultados já recebidos nesta página, por URL da requisição: voltar para um produto já visto
// (SPA) reaproveita a resposta sem nova GET. Limitado para não crescer sem fim numa sessão longa.
const CACHE_LIMIT = 100;
const cache = new Map<string, SchemaNodes>();

// Só os testes limpam o cache: em produção a página inteira é o escopo.
export function resetSchemaCache(): void {
  cache.clear();
}

function remember(key: string, nodes: SchemaNodes): void {
  if (cache.size >= CACHE_LIMIT) {
    const oldest = cache.keys().next().value;
    if (oldest !== undefined) cache.delete(oldest);
  }
  cache.set(key, nodes);
}

async function fetchComplementarySchema(
  apiBase: string,
  publicKey: string,
  product: ProductContext,
  connectionId: string | null,
  signal: AbortSignal | undefined,
): Promise<SchemaNodes> {
  // A busca no catálogo da IndexaAI é feita por SKU/GTIN/slug, nessa ordem (ver
  // GetProductSchemaUseCase no backend) — productId é só repassado como referência.
  if (!product.sku && !product.gtin && !product.slug && !product.slugPath) return null;

  const url = new URL(`${apiBase}/v1/metatag/schema`);
  if (product.productId) url.searchParams.set("productId", product.productId);
  if (product.sku) url.searchParams.set("sku", product.sku);
  if (product.gtin) url.searchParams.set("gtin", product.gtin);
  if (product.slug) url.searchParams.set("slug", product.slug);
  // Fallback do backend quando o slug (último segmento) não acha nada, ex: nomes com "/".
  if (product.slugPath) url.searchParams.set("slugPath", product.slugPath);
  if (connectionId) url.searchParams.set("connectionId", connectionId);

  const key = url.toString();
  if (cache.has(key)) return cache.get(key) ?? null;

  try {
    const res = await fetch(key, {
      headers: { "x-indexaai-key": publicKey },
      signal,
    });
    if (!res.ok) return null;
    const body = (await res.json()) as SchemaResponse;
    const nodes = body.schema ?? null;
    remember(key, nodes);
    return nodes;
  } catch {
    // inclui AbortError: navegação saiu da página antes da resposta
    return null;
  }
}

function injectLdJson(id: string, schema: Record<string, unknown>): void {
  const text = JSON.stringify(schema);
  const existing = document.getElementById(id);
  if (existing?.textContent === text) return;
  existing?.remove();

  const script = document.createElement("script");
  script.id = id;
  script.type = "application/ld+json";
  script.textContent = text;
  document.head.appendChild(script);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function hasProductType(value: unknown): boolean {
  const types = Array.isArray(value) ? value : [value];
  return types.some(
    (type) =>
      typeof type === "string" &&
      (type === "Product" || type.endsWith("/Product") || type.endsWith("#Product")),
  );
}

function hasSchemaType(node: Record<string, unknown>, type: string): boolean {
  const types = Array.isArray(node["@type"]) ? node["@type"] : [node["@type"]];
  return types.some(
    (candidate) =>
      typeof candidate === "string" &&
      (candidate === type || candidate.endsWith(`/${type}`) || candidate.endsWith(`#${type}`)),
  );
}

function isProductNode(value: unknown): value is Record<string, unknown> {
  return isRecord(value) && hasProductType(value["@type"]);
}

function collectSchemaNodes(value: unknown, result: Record<string, unknown>[], depth = 0): void {
  if (depth > 8 || value === null || typeof value !== "object") return;

  if (Array.isArray(value)) {
    for (const item of value) collectSchemaNodes(item, result, depth + 1);
    return;
  }

  const node = value as Record<string, unknown>;
  if (typeof node["@type"] === "string" || Array.isArray(node["@type"])) result.push(node);
  for (const child of Object.values(node)) collectSchemaNodes(child, result, depth + 1);
}

function isMeaningful(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (isRecord(value)) return Object.keys(value).length > 0;
  return true;
}

function arrayItemKey(value: unknown): string {
  if (!isRecord(value)) return `${typeof value}:${String(value)}`;

  for (const key of [
    "@id",
    "url",
    "propertyID",
    "name",
    "sku",
    "gtin",
    "gtin13",
    "gtin12",
    "gtin14",
    "gtin8",
  ]) {
    const item = value[key];
    if (typeof item === "string" && item.trim()) return `${key}:${item.trim().toLowerCase()}`;
  }

  return JSON.stringify(value);
}

/** Merge supplementally: keep all non-empty values already published by the store. */
function mergeSchemaValues(current: unknown, generated: unknown): unknown {
  if (!isMeaningful(current)) return generated;
  if (!isMeaningful(generated)) return current;

  if (Array.isArray(current) || Array.isArray(generated)) {
    const currentItems = Array.isArray(current) ? current : [current];
    const generatedItems = Array.isArray(generated) ? generated : [generated];
    const merged = [...currentItems];
    const keys = new Set(currentItems.map(arrayItemKey));

    for (const item of generatedItems) {
      const key = arrayItemKey(item);
      if (!keys.has(key)) {
        keys.add(key);
        merged.push(item);
      }
    }

    return merged;
  }

  if (isRecord(current) && isRecord(generated)) {
    const merged: Record<string, unknown> = { ...generated, ...current };
    for (const [key, value] of Object.entries(generated)) {
      merged[key] = key in current ? mergeSchemaValues(current[key], value) : value;
    }
    return merged;
  }

  return current;
}

function canonicalUrl(value: unknown): string | null {
  if (typeof value !== "string" || !value.trim()) return null;
  try {
    const url = new URL(value, window.location.href);
    return `${url.origin}${url.pathname.replace(/\/+$/, "")}`;
  } catch {
    return null;
  }
}

function productNodeUrls(node: Record<string, unknown>): string[] {
  const urls: string[] = [];
  for (const key of ["url", "@id"]) {
    if (typeof node[key] === "string") urls.push(node[key] as string);
  }

  const offers = Array.isArray(node.offers) ? node.offers : [node.offers];
  for (const offer of offers) {
    if (isRecord(offer) && typeof offer.url === "string") urls.push(offer.url);
  }

  return urls;
}

function productNodeIdentifiers(node: Record<string, unknown>): string[] {
  return ["productID", "sku", "mpn", "gtin", "gtin13", "gtin12", "gtin14", "gtin8"]
    .map((key) => node[key])
    .filter(
      (value): value is string | number => typeof value === "string" || typeof value === "number",
    )
    .map((value) => String(value).trim().toLowerCase())
    .filter(Boolean);
}

function isCurrentPageProduct(
  node: Record<string, unknown>,
  product: ProductContext,
  candidateCount: number,
): boolean {
  const currentUrl = canonicalUrl(window.location.href);
  const urls = productNodeUrls(node)
    .map(canonicalUrl)
    .filter((url): url is string => Boolean(url));
  if (currentUrl && urls.includes(currentUrl)) return true;

  const nodeIdentifiers = productNodeIdentifiers(node);
  const contextIdentifiers = [product.productId, product.sku, product.gtin]
    .filter((value): value is string => Boolean(value))
    .map((value) => value.trim().toLowerCase());
  if (contextIdentifiers.some((identifier) => nodeIdentifiers.includes(identifier))) return true;

  // A sole unlabelled Product node is the only safe fallback when the platform
  // omits both URL and identifiers from its native JSON-LD.
  return candidateCount === 1 && urls.length === 0 && nodeIdentifiers.length === 0;
}

function isProductItemType(value: string | null): boolean {
  if (!value) return false;
  return value.split(/\s+/).some((type) => {
    const normalized = type.trim();
    return (
      normalized === "Product" || normalized.endsWith("/Product") || normalized.endsWith("#Product")
    );
  });
}

function isProductMicrodataScope(element: Element): boolean {
  return element.hasAttribute("itemscope") && isProductItemType(element.getAttribute("itemtype"));
}

function directMicrodataProperties(root: Element, property: string): Element[] {
  const normalizedProperty = property.toLowerCase();
  return Array.from(root.querySelectorAll<Element>("[itemprop]")).filter((element) => {
    const properties = (element.getAttribute("itemprop") ?? "")
      .split(/\s+/)
      .map((candidate) => candidate.toLowerCase());
    return (
      properties.includes(normalizedProperty) &&
      element.parentElement?.closest("[itemscope]") === root
    );
  });
}

function microdataValue(element: Element): string {
  for (const attribute of ["content", "datetime", "href", "src", "data", "value"]) {
    const value = element.getAttribute(attribute);
    if (value !== null) return value.trim();
  }
  return (element.textContent ?? "").trim();
}

function hasMicrodataValue(element: Element): boolean {
  if (!element.hasAttribute("itemscope")) return Boolean(microdataValue(element));
  return Array.from(element.querySelectorAll<Element>("[itemprop]")).some((child) => {
    if (child.parentElement?.closest("[itemscope]") !== element) return false;
    return child.hasAttribute("itemscope")
      ? hasMicrodataValue(child)
      : Boolean(microdataValue(child));
  });
}

function microdataPropertyValues(root: Element, property: string): string[] {
  return directMicrodataProperties(root, property)
    .filter((element) => !element.hasAttribute("itemscope"))
    .map(microdataValue)
    .filter(Boolean);
}

function microdataProductIdentifiers(root: Element): string[] {
  return ["productID", "sku", "mpn", "gtin", "gtin13", "gtin12", "gtin14", "gtin8"]
    .flatMap((property) => microdataPropertyValues(root, property))
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);
}

function isCurrentMicrodataProduct(
  root: Element,
  product: ProductContext,
  candidateCount: number,
): boolean {
  const currentUrl = canonicalUrl(window.location.href);
  const urls = [root.getAttribute("itemid"), ...microdataPropertyValues(root, "url")]
    .map(canonicalUrl)
    .filter((url): url is string => Boolean(url));
  if (currentUrl && urls.includes(currentUrl)) return true;

  const nodeIdentifiers = microdataProductIdentifiers(root);
  const contextIdentifiers = [product.productId, product.sku, product.gtin]
    .filter((value): value is string => Boolean(value))
    .map((value) => value.trim().toLowerCase());
  if (contextIdentifiers.some((identifier) => nodeIdentifiers.includes(identifier))) return true;

  return candidateCount === 1 && urls.length === 0 && nodeIdentifiers.length === 0;
}

function schemaTypeUrl(value: unknown): string | null {
  if (typeof value !== "string" || !value.trim()) return null;
  const type = value.trim();
  if (type.startsWith("http://") || type.startsWith("https://")) return type;
  return `https://schema.org/${type}`;
}

function appendMicrodataValue(
  root: Element,
  property: string,
  value: unknown,
  added: Element[],
): void {
  if (!isMeaningful(value)) return;

  if (Array.isArray(value)) {
    for (const item of value) appendMicrodataValue(root, property, item, added);
    return;
  }

  if (isRecord(value)) {
    const element = document.createElement("span");
    element.setAttribute("itemprop", property);
    element.setAttribute("itemscope", "");
    const itemType = schemaTypeUrl(value["@type"]);
    if (itemType) element.setAttribute("itemtype", itemType);
    root.appendChild(element);
    added.push(element);

    for (const [childProperty, childValue] of Object.entries(value)) {
      if (childProperty.startsWith("@")) continue;
      appendMicrodataValue(element, childProperty, childValue, added);
    }
    return;
  }

  const element = document.createElement("meta");
  element.setAttribute("itemprop", property);
  element.setAttribute("content", String(value));
  root.appendChild(element);
  added.push(element);
}

function microdataArrayItemKey(element: Element): string {
  const nestedProperties = Array.from(element.querySelectorAll<Element>("[itemprop]")).filter(
    (child) => child.parentElement?.closest("[itemscope]") === element,
  );
  const nestedValue = (key: string): string | undefined =>
    nestedProperties
      .find((child) =>
        (child.getAttribute("itemprop") ?? "")
          .toLowerCase()
          .split(/\s+/)
          .includes(key.toLowerCase()),
      )
      ?.textContent?.trim() ||
    nestedProperties
      .find((child) =>
        (child.getAttribute("itemprop") ?? "")
          .toLowerCase()
          .split(/\s+/)
          .includes(key.toLowerCase()),
      )
      ?.getAttribute("content")
      ?.trim();

  const value: Record<string, unknown> = {};
  for (const key of [
    "@id",
    "url",
    "propertyID",
    "name",
    "sku",
    "gtin",
    "gtin13",
    "gtin12",
    "gtin14",
    "gtin8",
  ]) {
    const found = key.startsWith("@") ? undefined : nestedValue(key);
    if (found) value[key] = found;
  }
  if (Object.keys(value).length === 0)
    return `${typeof microdataValue(element)}:${microdataValue(element)}`;
  return arrayItemKey(value);
}

function mergeProductIntoMicrodata(root: Element, generated: Record<string, unknown>): void {
  const added: Element[] = [];
  for (const [property, value] of Object.entries(generated)) {
    if (property.startsWith("@") || !isMeaningful(value)) continue;

    if (property === "identifier" || property === "additionalProperty") {
      const existingItems = directMicrodataProperties(root, property);
      const existingKeys = new Set(existingItems.map(microdataArrayItemKey));
      const generatedItems = Array.isArray(value) ? value : [value];
      for (const item of generatedItems) {
        const key = isRecord(item) ? arrayItemKey(item) : `${typeof item}:${String(item)}`;
        if (existingKeys.has(key)) continue;
        existingKeys.add(key);
        appendMicrodataValue(root, property, item, added);
      }
      continue;
    }

    const existingItems = directMicrodataProperties(root, property);
    if (existingItems.some(hasMicrodataValue)) continue;
    appendMicrodataValue(root, property, value, added);
  }

  if (added.length > 0) addedMicrodataElements.push({ root, elements: added });
}

function matchingPageMicrodataProducts(product: ProductContext): Element[] {
  const products = Array.from(document.querySelectorAll<Element>("[itemscope][itemtype]")).filter(
    isProductMicrodataScope,
  );
  return products.filter((root) => isCurrentMicrodataProduct(root, product, products.length));
}

function rewriteSchemaNodes(
  value: unknown,
  replacements: Map<Record<string, unknown>, Record<string, unknown>>,
  emittedNodes: Set<Record<string, unknown>>,
): unknown | typeof REMOVE_NODE {
  if (Array.isArray(value)) {
    return value
      .map((item) => rewriteSchemaNodes(item, replacements, emittedNodes))
      .filter((item) => item !== REMOVE_NODE);
  }

  if (!isRecord(value)) return value;
  const replacement = replacements.get(value);
  if (replacement) {
    if (emittedNodes.has(replacement)) return REMOVE_NODE;
    emittedNodes.add(replacement);
    return replacement;
  }

  const rewritten: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value)) {
    const next = rewriteSchemaNodes(child, replacements, emittedNodes);
    if (next !== REMOVE_NODE) rewritten[key] = next;
  }
  return rewritten;
}

function mergeWithPageSchemas(
  nodes: Record<string, unknown>[],
  product: ProductContext,
): Record<string, unknown>[] {
  const generatedProducts = nodes.filter(isProductNode);
  const generatedFaqPages = nodes.filter((node) => hasSchemaType(node, "FAQPage"));
  if (generatedProducts.length === 0 && generatedFaqPages.length === 0) return nodes;

  const scripts = Array.from(
    document.querySelectorAll<HTMLScriptElement>('script[type="application/ld+json"]'),
  ).filter((script) => script.id !== PRODUCT_SCHEMA_ID);
  const parsedScripts: ParsedSchemaScript[] = [];
  const pageNodes: Record<string, unknown>[] = [];

  for (const script of scripts) {
    const originalText = script.textContent ?? "";
    try {
      const value: unknown = JSON.parse(originalText);
      const schemaNodes: Record<string, unknown>[] = [];
      collectSchemaNodes(value, schemaNodes);
      parsedScripts.push({ script, originalText, value, schemaNodes });
      pageNodes.push(...schemaNodes);
    } catch {
      // Ignore unrelated or invalid LD-JSON and keep searching other scripts.
    }
  }

  const replacements = new Map<Record<string, unknown>, Record<string, unknown>>();
  const consumedGeneratedNodes = new Set<Record<string, unknown>>();
  const pageProducts = pageNodes.filter(isProductNode);
  const matchingProducts = pageProducts.filter((node) =>
    isCurrentPageProduct(node, product, pageProducts.length),
  );

  if (generatedProducts.length > 0 && matchingProducts.length > 0) {
    let mergedProduct = matchingProducts[0] as Record<string, unknown>;
    for (const pageNode of matchingProducts.slice(1)) {
      mergedProduct = mergeSchemaValues(mergedProduct, pageNode) as Record<string, unknown>;
    }
    for (const generatedNode of generatedProducts) {
      mergedProduct = mergeSchemaValues(mergedProduct, generatedNode) as Record<string, unknown>;
      consumedGeneratedNodes.add(generatedNode);
    }
    for (const pageNode of matchingProducts) replacements.set(pageNode, mergedProduct);
  }

  if (generatedProducts.length > 0 && matchingProducts.length === 0) {
    const matchingMicrodataProducts = matchingPageMicrodataProducts(product);
    const matchingMicrodataProduct = matchingMicrodataProducts[0];
    if (matchingMicrodataProduct) {
      for (const generatedNode of generatedProducts) {
        mergeProductIntoMicrodata(matchingMicrodataProduct, generatedNode);
        consumedGeneratedNodes.add(generatedNode);
      }
    }
  }

  const matchingFaqPages = pageNodes.filter((node) => hasSchemaType(node, "FAQPage"));
  if (generatedFaqPages.length > 0 && matchingFaqPages.length > 0) {
    let mergedFaqPage = matchingFaqPages[0] as Record<string, unknown>;
    for (const pageNode of matchingFaqPages.slice(1)) {
      mergedFaqPage = mergeSchemaValues(mergedFaqPage, pageNode) as Record<string, unknown>;
    }
    for (const generatedNode of generatedFaqPages) {
      mergedFaqPage = mergeSchemaValues(mergedFaqPage, generatedNode) as Record<string, unknown>;
      consumedGeneratedNodes.add(generatedNode);
    }
    for (const pageNode of matchingFaqPages) replacements.set(pageNode, mergedFaqPage);
  }

  if (replacements.size === 0) return nodes.filter((node) => !consumedGeneratedNodes.has(node));

  const emittedNodes = new Set<Record<string, unknown>>();
  for (const { script, originalText, value, schemaNodes } of parsedScripts) {
    if (!schemaNodes.some((node) => replacements.has(node))) continue;

    const rewritten = rewriteSchemaNodes(value, replacements, emittedNodes);
    const nextValue =
      rewritten === REMOVE_NODE ? { "@context": "https://schema.org", "@graph": [] } : rewritten;
    const mergedText = JSON.stringify(nextValue);
    if (mergedText === originalText) continue;

    script.textContent = mergedText;
    const previousModification = modifiedPageSchemaScripts.find(
      (modification) => modification.script === script,
    );
    if (previousModification) {
      previousModification.originalText = originalText;
      previousModification.mergedText = mergedText;
    } else {
      modifiedPageSchemaScripts.push({ script, originalText, mergedText });
    }
  }

  return nodes.filter((node) => !consumedGeneratedNodes.has(node));
}

function restorePageSchemaScripts(): void {
  for (const { script, originalText, mergedText } of modifiedPageSchemaScripts.splice(0)) {
    // A platform may replace its own JSON-LD during SPA navigation. Restore
    // only scripts that still contain the exact version we wrote.
    if (script.isConnected && script.textContent === mergedText) script.textContent = originalText;
  }
}

function restorePageMicrodata(): void {
  for (const { elements } of addedMicrodataElements.splice(0)) {
    for (const element of elements) element.remove();
  }
}

function isPageSchemaScript(node: Node | null): boolean {
  if (!node) return false;

  const script =
    node instanceof HTMLScriptElement
      ? node
      : (node.parentElement?.closest(
          'script[type="application/ld+json"]',
        ) as HTMLScriptElement | null);

  return script?.type === "application/ld+json" && script.id !== PRODUCT_SCHEMA_ID;
}

function nodeContainsPageSchema(node: Node): boolean {
  if (isPageSchemaScript(node)) return true;
  if (!(node instanceof Element)) return false;

  return Array.from(node.querySelectorAll('script[type="application/ld+json"]')).some(
    (script) => script.id !== PRODUCT_SCHEMA_ID,
  );
}

function isPageMicrodataProduct(node: Node | null): boolean {
  if (!node) return false;
  let element = node instanceof Element ? node : node.parentElement;
  while (element) {
    if (isProductMicrodataScope(element)) return true;
    element = element.parentElement;
  }
  return false;
}

function nodeContainsPageMicrodataProduct(node: Node): boolean {
  if (!(node instanceof Element)) return false;
  if (isProductMicrodataScope(node)) return true;
  return Array.from(node.querySelectorAll("[itemscope][itemtype]")).some(isProductMicrodataScope);
}

function schemaForNodes(nodes: Record<string, unknown>[]): Record<string, unknown> | null {
  if (nodes.length === 0) return null;
  if (nodes.length === 1 && nodes[0]) return nodes[0];
  return { "@context": "https://schema.org", "@graph": nodes };
}

function applyActivePageSchema(): void {
  if (!activePageSchema) return;

  const remainingNodes = mergeWithPageSchemas(activePageSchema.nodes, activePageSchema.product);
  const schema = schemaForNodes(remainingNodes);
  if (schema) injectLdJson(PRODUCT_SCHEMA_ID, schema);
  else document.getElementById(PRODUCT_SCHEMA_ID)?.remove();
}

function observePageSchemas(): void {
  if (!activePageSchema || typeof MutationObserver === "undefined") return;

  pageSchemaObserver?.disconnect();
  pageSchemaObserver = new MutationObserver((mutations) => {
    const pageSchemaChanged = mutations.some(
      (mutation) =>
        isPageSchemaScript(mutation.target) ||
        isPageMicrodataProduct(mutation.target) ||
        Array.from(mutation.addedNodes).some(nodeContainsPageSchema) ||
        Array.from(mutation.removedNodes).some(nodeContainsPageSchema) ||
        Array.from(mutation.addedNodes).some(nodeContainsPageMicrodataProduct) ||
        Array.from(mutation.removedNodes).some(nodeContainsPageMicrodataProduct),
    );

    if (pageSchemaChanged) applyActivePageSchema();
  });
  pageSchemaObserver.observe(document.documentElement, {
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: [
      "itemscope",
      "itemtype",
      "itemprop",
      "content",
      "datetime",
      "href",
      "src",
      "data",
      "value",
    ],
    subtree: true,
  });
}

export function removeProductSchema(): void {
  pageSchemaObserver?.disconnect();
  pageSchemaObserver = null;
  activePageSchema = null;
  restorePageSchemaScripts();
  restorePageMicrodata();
  document.getElementById(PRODUCT_SCHEMA_ID)?.remove();
}

export async function injectProductSchema(
  apiBase: string,
  publicKey: string,
  product: ProductContext,
  connectionId: string | null = null,
  signal?: AbortSignal,
): Promise<void> {
  const nodes = await fetchComplementarySchema(apiBase, publicKey, product, connectionId, signal);
  // Se a página mudou enquanto a resposta vinha, ela descreve outro produto: não injeta.
  if (signal?.aborted) return;
  if (!nodes || nodes.length === 0) return;

  // Revert any previous page's merge before looking for the native schema to
  // augment. This also keeps repeated calls idempotent on the same PDP.
  removeProductSchema();

  activePageSchema = { nodes, product };
  const remainingNodes = mergeWithPageSchemas(nodes, product);
  const schema = schemaForNodes(remainingNodes);
  if (schema) injectLdJson(PRODUCT_SCHEMA_ID, schema);
  observePageSchemas();
}
